<template>
  <v-card v-if="hasSocket" class="chat-card">
    <v-card-title class="bar-title"> Contacter Hublot </v-card-title>
    <v-card-text>
      <div id="chat-messages" class="chat-messages">
        <template v-for="(sortedMessage, i) in sortedMessages">
          <div v-if="i !== 'undefined'" :key="'hr-' + i" class="hr-sect">
            <span class="message-date">{{ i }} </span>
          </div>
          <div
            v-for="message in sortedMessage"
            :key="'message-' + message.id"
            class="message-row"
            :class="
              message.source === 'crm'
                ? 'message-row-support'
                : 'message-row-magasin'
            "
          >
            <div
              class="chat-bubble"
              :class="
                message.source === 'crm' ? 'bubble-support' : 'bubble-magasin'
              "
            >
              <div class="chat-message-header">
                <span class="chat-message-username">
                  {{ authorName(message) }}
                </span>
                <span class="chat-message-hour">
                  {{ message.date | formatHour }}
                </span>
              </div>
              <v-divider />
              <!-- eslint-disable vue/singleline-html-element-content-newline -->
              <!-- `.chat-message` est en `white-space: pre-wrap` : garder le
                   contenu collé aux balises, sinon Vue rend l'indentation du
                   template telle quelle. -->
              <div
                v-if="hasText(message)"
                class="chat-message chat-message-text"
              >{{ message.text }}</div>
              <div
                v-else-if="message.command && message.command.length > 0"
                class="chat-message chat-message-command"
              >Commande envoyée par le support Hublot</div>
              <div v-if="hasAttachment(message)" class="chat-message">
                <ChatImage :date="message.date" :file="message.attachment" />
              </div>
            </div>
          </div>
        </template>
      </div>
      <v-divider />
      <v-row no-gutters align="center">
        <v-menu top offset-y nudge-top="50">
          <template #activator="{ on }">
            <v-btn
              :elevation="selectedEmploye ? 6 : 2"
              class="chat-employe-icon"
              icon
              :outlined="!selectedEmploye"
              :color="
                selectedEmploye !== null
                  ? selectedEmploye.color !== null
                    ? selectedEmploye.color
                    : 'primary darken-2'
                  : 'grey darken-2'
              "
              @click="toggleEmployee"
              v-on="on"
            >
              <v-icon v-if="selectedEmploye === null"> people </v-icon>
              <span v-else>{{ selectedEmploye.inits }}</span>
            </v-btn>
          </template>
          <v-card>
            <v-col class="employe-list scroller-bar">
              <v-list v-for="employe in employesList" :key="employe.id" dense>
                <v-list-item>
                  <v-list-item-avatar>
                    <v-icon :color="employe.color"> people </v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content @click="selectEmploye(employe)">
                    <v-list-item-title>
                      <b
                        v-if="
                          selectedEmploye &&
                          employe.name === selectedEmploye.name
                        "
                        >{{ employe.name }}</b
                      >
                      <span v-else>{{ employe.name }}</span>
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-icon
                      v-if="
                        selectedEmploye && selectedEmploye.id === employe.id
                      "
                      @click="removeSelectedEmploye()"
                    >
                      close
                    </v-icon>
                  </v-list-item-action>
                </v-list-item>
                <v-divider :key="'divider-' + employe.id" />
              </v-list>
            </v-col>
          </v-card>
        </v-menu>
        <v-row no-gutters class="ml-2 mr-2">
          <v-textarea
            v-model="text"
            rows="1"
            row-height="12"
            :auto-grow="true"
            class="input-message-text"
            label="Entrez votre message"
            clearable
            :disabled="isSending"
            @keydown.enter.exact.prevent="sendMessage()"
          />
        </v-row>
        <v-btn
          text
          icon
          color="primary"
          :disabled="!text || text.length < 1"
          @click="sendMessage()"
        >
          <v-icon size="30"> send </v-icon>
        </v-btn>
      </v-row>
    </v-card-text>
  </v-card>
  <v-card
    v-else
    class="d-flex flex-column align-center justify-center"
    style="height: 84vh; width: 78vw"
    color="grey-darken-4"
    elevation="0"
  >
    <v-icon size="80" color="red lighten-1">signal_wifi_off</v-icon>

    <div class="text-center mt-4 px-4">
      <h2 class="text-h6 font-weight-medium">
        Pas de connexion
        <pre>Service Chat indisponible</pre>
        <br /><br />
        Le chat ne peut pas se charger pour l'instant
      </h2>
    </div>
  </v-card>
</template>

<script>
import groupby from 'lodash.groupby'
import moment from 'moment'
import { mapGetters, mapState } from 'vuex'
import ChatImage from '@/components/chatImage.vue'

export default {
  components: { ChatImage },
  props: ['dialog'],
  data() {
    return {
      menu: false,
      text: '',
      selectedEmploye: null,
      employeState: false,
      isSending: false,
      socketCheckInterval: 0
    }
  },
  computed: {
    hasSocket() {
      const socket = this.getSocket
      const isSocketConnected = socket.connected ?? false
      return isSocketConnected
    },
    employesList() {
      return this.employees.map(employe => ({
        id: employe.idEmployees,
        color: employe.color,
        name: `${employe.prenom} ${employe.nom}`,
        inits: employe.prenom.charAt(0) + employe.nom.charAt(0)
      }))
    },
    sortedMessages() {
      // 1. Flatten
      const flatItems = Object.values([...this.messages]).flat()

      // Grouper par date formatée ou "Sans date"
      const grouped = groupby(flatItems, item => (item.date ? moment(item.date).format('YYYY-MM-DD') : 'Sans date'))

      // Trier les messages dans chaque jour
      Object.entries(grouped).forEach(([key, items]) => {
        grouped[key] = items.sort((a, b) => moment(a.date).diff(moment(b.date)))
      })

      // Trier les groupes : "Sans date" d'abord, puis les autres par date décroissante
      const sortedGroups = [
        ...(grouped['Sans date'] ? [['Sans date', grouped['Sans date']]] : []),
        ...Object.entries(grouped)
          .filter(([key]) => key !== 'Sans date')
          .sort((a, b) => moment(a[0]).diff(moment(b[0])))
      ]

      // Reformater les clés avec format intelligent
      const final = Object.fromEntries(
        sortedGroups.map(([key, items]) => {
          const label = key === 'Sans date' ? 'Sans date' : this.formatSmartDate(key)
          return [label, items]
        })
      )

      return final
    },
    ...mapGetters(['getSocket']),
    ...mapState({
      messages: state => state.chat.messages,
      client: state => state.authentication.client,
      employees: state => state.notilus.employees,
      userProfile: state => state.authentication.userProfile,
    })
  },
  watch: {
    dialog() {
      if (this.dialog) {
        this.scrollTop()
      }
    },
    messages() {
      this.scrollTop()
    }
  },
  created() {
    this.scrollTop()
    this.$store.dispatch('getEmployees')

    this.socketCheckInterval = setInterval(() => {
      if (!this.hasSocket) {
        console.warn('Socket déconnecté, tentative de reconnexion...')
        this.$store.dispatch('joinAndListen', this.client.serial)
      }
    }, 10000) // toutes les 10 secondes
  },
  beforeDestroy() {
    clearInterval(this.socketCheckInterval)
  },
  methods: {
    authorName(message) {
      if (message.username) return message.username
      return message.source === 'crm' ? 'Support Hublot' : 'Magasin'
    },
    hasText(message) {
      return Boolean(message.text) && message.text.trim().length > 0
    },
    hasAttachment(message) {
      return Boolean(message.attachment) && message.attachment !== 'null'
    },
    toggleEmployee() {
      this.employeState = !this.employeState
    },
    selectEmploye(employe) {
      this.selectedEmploye = employe
    },
    removeSelectedEmploye() {
      this.selectedEmploye = null
    },
    formatSmartDate(rawDate) {
      const date = moment(rawDate)
      const currentYear = moment().year()

      return date.year() === currentYear ? date.format('DD/MM') : date.format('DD/MM/YYYY')
    },
    scrollTop() {
      if (this.dialog) {
        if (this.messages.filter(message => message.source === 'crm' && message.seen_by === null).length > 1) {
          const lastMessageId = this.messages
            .filter(message => message.source === 'crm' && message.seen_by === null)
            .sort((a, b) => new Date(b.date) - new Date(a.date))[0].id
          this.$store.dispatch('setRead', lastMessageId)
        }
      }
      setTimeout(() => {
        const chatMessage = document.getElementById('chat-messages')
        if (chatMessage) {
          chatMessage.scrollTop = chatMessage.scrollHeight
        }
      }, 200)
    },
    sendMessage() {
      if (this.isSending) return // Empêche les appels multiples
      const text = (this.text || '').replace(/^\s+|\s+$/g, '')
      if (text.length < 1) return
      this.isSending = true
      let username = null;
      if (this.selectedEmploye) {
        username = this.selectedEmploye.name;
      } else if (this.userProfile) {
        username = `${this.userProfile.firstName} ${this.userProfile.lastName}`;
      }
      const payload = {
        serial: this.client.serial,
        text,
        mode: 'support',
        current_route: this.$route.path,
        source: 'magasin',
        username: username
      }

      this.$store
        .dispatch('sendMessage', payload)
        .then(() => {
          this.$store.dispatch('loadMessages')
          this.text = ''
        })
        .finally(() => {
          this.isSending = false // Réautorise l'envoi
        })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/vars";
@import "@/assets/scss/scroller";

.chat-card {
  z-index: 99 !important;
}

.chat-messages {
  display: flex;
  flex-direction: column;
  max-height: 52vh;
  overflow: auto;
  padding: 12px 16px;
  background-color: white;
}

.message-row {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  width: 100%;
}

.message-row-support {
  justify-content: flex-start;
}

.message-row-magasin {
  justify-content: flex-end;
}

.chat-bubble {
  display: inline-block;
  box-sizing: border-box;
  min-width: 280px;
  max-width: 80%;
  margin: 10px 0;
  padding: 10px 18px;
  font-weight: 400;
}

.bubble-support {
  border-radius: 15px 15px 15px 10px;
  background-color: #f0f0f0;
  color: #222;
}

.bubble-magasin {
  border-radius: 15px 15px 10px 15px;
  background-color: $primary;
  color: #fff;
}

.bubble-magasin ::v-deep .v-divider {
  border-color: rgba(255, 255, 255, 0.35);
}

.chat-message-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 2px;
  font-size: 0.95em;
  font-weight: 600;
}

.chat-message-hour {
  padding-left: 12px;
  font-weight: 400;
  white-space: nowrap;
  opacity: 0.75;
}

.chat-message {
  padding: 5px 10px;
  text-align: left;
  white-space: pre-wrap;
  word-break: break-word;
}

.chat-message-text {
  font-weight: 400;
}

.chat-message-command {
  font-style: italic;
  opacity: 0.9;
}

.hr-sect {
  display: flex;
  flex-basis: 100%;
  align-items: center;
  width: 100%;
  margin: 18px 0 12px 0;
  color: #888;
  font-size: 13px;
  font-weight: 500;
}

.hr-sect::before,
.hr-sect::after {
  content: "";
  flex-grow: 1;
  height: 1.5px;
  margin: 0 16px;
  background: #e0e0e0;
  border-radius: 2px;
}

.message-date {
  text-transform: uppercase;
  padding: 2px 18px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #f7f7f7;
  color: #666;
  font-weight: 600;
  font-size: 1rem;
  letter-spacing: 0.04em;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.employe-list {
  max-height: 15vw;
  overflow-y: scroll;
}
</style>
